import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingCart, TrendingUp, TrendingDown, Plus,
  Calendar, Building2, FileText, Package, ChevronRight,
  AlertTriangle, CheckCircle, Trash2,
} from 'lucide-react';
import { usePurchaseOrders, useApplyStock, useDeleteOrder } from '../hooks/usePurchases';
import { usePriceOverview } from '../hooks/usePurchases';
import { toast } from 'react-toastify';


// ── helpers ────────────────────────────────────────────────────────────────────
const fmt = (n: number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });

function TrendBadge({ pct }: { pct: number | null }) {
  if (pct === null) return <span className="text-xs text-muted">—</span>;
  if (pct > 0) return (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">
      <TrendingUp size={10} /> +{pct}%
    </span>
  );
  if (pct < 0) return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
      <TrendingDown size={10} /> {pct}%
    </span>
  );
  return <span className="text-xs text-muted rounded-full bg-surface-muted px-2 py-0.5">Sin cambio</span>;
}

const PurchasesDashboard: React.FC = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const { data: ordersRes, isLoading } = usePurchaseOrders(filters);
  const { data: overview } = usePriceOverview();
  const applyStock = useApplyStock();
  const deleteOrder = useDeleteOrder();
  const overviewList = Array.isArray(overview) ? overview : [];

  const orders = ordersRes?.data ?? [];

  const handleApplyStock = async (id: number) => {
    try {
      await applyStock.mutateAsync(id);
      toast.success('Stock aplicado al inventario');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Error al aplicar stock');
    }
  };

  const handleDeleteOrder = async (id: number) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta factura? Se revertirán los movimientos de inventario asociados y se restará el stock cargado.')) {
      try {
        await deleteOrder.mutateAsync(id);
        toast.success('Factura de compra eliminada correctamente y stock revertido');
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? 'Error al eliminar la factura');
      }
    }
  };


  // KPIs
  const totalSpent = orders.reduce((s, o) => s + o.total, 0);
  const rising = overviewList.filter(o => (o.stats.total_change ?? 0) > 5).length;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-8">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted">Módulo</p>
            <h1 className="text-3xl font-bold text-[color:var(--color-text)]">Compras</h1>
            <p className="mt-1 text-sm text-muted">Registro de facturas y seguimiento de precios</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link
              to="/purchases/suppliers"
              className="inline-flex h-10 items-center gap-2 rounded-full border border-divider px-4 text-sm font-semibold transition hover:bg-surface-muted"
            >
              <Building2 size={15} /> Proveedores
            </Link>
            <Link
              to="/purchases/prices"
              className="inline-flex h-10 items-center gap-2 rounded-full border border-divider px-4 text-sm font-semibold transition hover:bg-surface-muted"
            >
              <TrendingUp size={15} /> Precios
            </Link>
            <Link
              to="/purchases/expenses"
              className="inline-flex h-10 items-center gap-2 rounded-full border border-divider px-4 text-sm font-semibold transition hover:bg-surface-muted"
            >
              <TrendingDown size={15} /> Gastos
            </Link>
            <Link
              to="/purchases/new"
              className="inline-flex h-10 items-center gap-2 rounded-full bg-primary px-4 text-sm font-bold text-white shadow hover:opacity-90"
            >
              <Plus size={15} /> Nueva factura
            </Link>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <div className="rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 p-5 text-white shadow-lg">
            <p className="text-xs font-bold uppercase tracking-widest opacity-80">Facturas</p>
            <p className="mt-1 text-3xl font-bold">{ordersRes?.total ?? '…'}</p>
            <p className="mt-1 text-xs opacity-70">órdenes registradas</p>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-violet-500 to-violet-700 p-5 text-white shadow-lg">
            <p className="text-xs font-bold uppercase tracking-widest opacity-80">Gastado</p>
            <p className="mt-1 text-2xl font-bold">{fmt(totalSpent)}</p>
            <p className="mt-1 text-xs opacity-70">en el período actual</p>
          </div>
          <div className={`rounded-2xl p-5 text-white shadow-lg col-span-2 sm:col-span-1 ${rising > 0 ? 'bg-gradient-to-br from-red-500 to-red-700' : 'bg-gradient-to-br from-emerald-500 to-emerald-700'}`}>
            <p className="text-xs font-bold uppercase tracking-widest opacity-80">Insumos con alza</p>
            <p className="mt-1 text-3xl font-bold">{rising}</p>
            <p className="mt-1 text-xs opacity-70">{rising > 0 ? 'subieron más de 5%' : 'precios estables'}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <input
            type="date"
            className="rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
            aria-label="Desde"
          />
          <input
            type="date"
            className="rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
            aria-label="Hasta"
          />
          <input
            type="text"
            placeholder="Buscar factura..."
            className="rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Orders list */}
          <div className="lg:col-span-2 space-y-3">
            <h2 className="text-base font-bold text-[color:var(--color-text)]">Facturas recientes</h2>
            {isLoading ? (
              <div className="space-y-3">{[...Array(4)].map((_, i) => (
                <div key={i} className="h-20 animate-pulse rounded-2xl bg-surface-muted" />
              ))}</div>
            ) : orders.length === 0 ? (
              <div className="rounded-2xl border border-divider bg-surface p-10 text-center">
                <ShoppingCart size={40} className="mx-auto mb-3 text-muted opacity-40" />
                <p className="text-sm text-muted">No hay facturas. ¡Registra la primera!</p>
                <Link to="/purchases/new" className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-primary hover:underline">
                  <Plus size={14} /> Nueva factura
                </Link>
              </div>
            ) : (
              orders.map(order => (
                <div key={order.id} className="group rounded-2xl border border-divider bg-surface p-4 shadow-sm transition hover:border-primary/40 hover:shadow-md">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <FileText size={18} />
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-[color:var(--color-text)]">
                          {order.supplier?.name ?? '—'}
                          {order.invoice_number && <span className="ml-2 text-xs text-muted">#{order.invoice_number}</span>}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Calendar size={11} className="text-muted" />
                          <span className="text-xs text-muted">{fmtDate(order.purchased_at)}</span>
                          <Package size={11} className="text-muted ml-1" />
                          <span className="text-xs text-muted">{order.items_count ?? '?'} ítems</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-bold text-[color:var(--color-text)]]">{fmt(order.total)}</p>
                      <div className="mt-1 flex items-center gap-2 justify-end">
                        {order.stock_applied ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                            <CheckCircle size={10} /> Stock aplicado
                          </span>
                        ) : (
                          <button
                            onClick={() => handleApplyStock(order.id)}
                            disabled={applyStock.isPending}
                            className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700 hover:bg-amber-200 transition"
                          >
                            <AlertTriangle size={10} /> Aplicar stock
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteOrder(order.id)}
                          disabled={deleteOrder.isPending}
                          className="text-red-500 hover:text-red-700 transition disabled:opacity-50 p-1"
                          title="Eliminar factura y revertir stock"
                        >
                          <Trash2 size={16} />
                        </button>
                        <Link to={`/purchases/orders/${order.id}`} className="text-primary hover:underline">
                          <ChevronRight size={16} />
                        </Link>
                      </div>
                    </div>

                  </div>
                </div>
              ))
            )}
          </div>

          {/* Price overview */}
          <div className="rounded-2xl border border-divider bg-surface p-5 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-[color:var(--color-text)]">Variación de precios</h2>
              <Link to="/purchases/prices" className="text-xs text-primary hover:underline flex items-center gap-1">
                Ver todo <ChevronRight size={12} />
              </Link>
            </div>
            {(overview ?? []).slice(0, 8).map(item => (
              <div key={item.inventory_item_id} className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-[color:var(--color-text)] truncate">{item.item_name}</p>
                  <p className="text-[10px] text-muted">{fmt(item.stats.last)}/{item.item_unit}</p>
                </div>
                <TrendBadge pct={item.stats.total_change} />
              </div>
            ))}
            {!overview && (
              <div className="space-y-2">{[...Array(5)].map((_, i) => (
                <div key={i} className="h-8 animate-pulse rounded-xl bg-surface-muted" />
              ))}</div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default PurchasesDashboard;
